import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, LogIn, LogOut, PackagePlus, PackageOpen, Truck,
  PenLine, RefreshCw, AlertTriangle, Building2, Users, Boxes,
} from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import usePagination from "@/hooks/usePagination";

const ICONS = {
  "auth.login": LogIn,
  "auth.login.failed": AlertTriangle,
  "auth.logout": LogOut,
  "tenant.create": Building2,
  "branch.create": Boxes,
  "user.create": Users,
  "parcel.inward.create": PackagePlus,
  "parcel.outward.create": PackageOpen,
  "parcel.status.update": RefreshCw,
  "parcel.forward": Truck,
  "parcel.deliver": PenLine,
};

const CLR = {
  "auth.login": "text-emerald-600 border-emerald-500 bg-emerald-500/10",
  "auth.login.failed": "text-destructive border-destructive bg-destructive/10",
  "auth.logout": "text-muted-foreground border-border bg-muted/50",
  "parcel.deliver": "text-primary border-primary bg-primary/10",
};

function ActionChip({ action }) {
  const Icon = ICONS[action] || ShieldCheck;
  const cls = CLR[action] || "text-foreground border-border bg-card";
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] mono uppercase tracking-widest ${cls}`}>
      <Icon size={11} />
      {action}
    </span>
  );
}

export default function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [days, setDays] = useState(30);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const { pageItems, Pagination } = usePagination(items);

  const load = async () => {
    const { data } = await api.get("/audit", {
      params: {
        days,
        action: action || undefined,
        actor: actor || undefined,
        limit: 300,
      },
    });
    setItems(data.items);
    setActions(data.actions);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, action]);

  const totalSuccess = items.filter(i => i.outcome !== "failure").length;
  const totalFailure = items.length - totalSuccess;

  return (
    <PageShell
      title="Audit Log."
      overline={<><ShieldCheck size={12}/> Enterprise-grade audit trail</>}
      subtitle="Every consequential action — signed, timestamped, IP-stamped."
      testid="audit"
      actions={
        <>
          <div className="inline-flex border border-border">
            {[1,7,30,90].map(dd => (
              <button key={dd} data-testid={`audit-days-${dd}`} onClick={()=>setDays(dd)}
                className={`px-3 py-1 text-xs mono uppercase tracking-widest ${days===dd ? "bg-primary text-primary-foreground" : ""}`}>{dd}d</button>
            ))}
          </div>
          <Button data-testid="audit-refresh" onClick={load} className="rounded-md bg-foreground text-background hover-brut shadow-brut">
            <RefreshCw size={14} className="mr-2"/> Refresh
          </Button>
        </>
      }
    >

      {/* KPI */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Events" value={items.length} testid="audit-total" accent />
        <Kpi label="Successes" value={totalSuccess} testid="audit-success" />
        <Kpi label="Failures" value={totalFailure} testid="audit-failure" />
        <Kpi label="Actions tracked" value={actions.length} testid="audit-distinct" />
      </section>

      {/* Filters */}
      <div className="border border-border bg-card p-4 flex flex-wrap items-center gap-3">
        <div className="overline">Filter</div>
        <div className="flex flex-wrap gap-1">
          <button data-testid="audit-action-all" onClick={()=>setAction("")}
            className={`text-[10px] mono uppercase tracking-widest px-2 py-1 border ${!action ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>ALL</button>
          {actions.map(a => (
            <button key={a.action} data-testid={`audit-action-${a.action}`} onClick={()=>setAction(a.action)}
              className={`text-[10px] mono uppercase tracking-widest px-2 py-1 border ${action===a.action ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
              {a.action} <span className="opacity-60">· {a.count}</span>
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px] flex items-center gap-2">
          <Input data-testid="audit-actor" placeholder="Filter by actor name or id"
            value={actor} onChange={(e)=>setActor(e.target.value)}
            onKeyDown={(e)=>{ if(e.key==="Enter") load(); }}
            className="rounded-md border-input" />
          <Button data-testid="audit-actor-apply" size="sm" onClick={load} variant="outline" className="rounded-md">Apply</Button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left overline">
              <th className="p-4">When</th>
              <th className="p-4">Actor</th>
              <th className="p-4">Action</th>
              <th className="p-4">Resource</th>
              <th className="p-4">IP</th>
              <th className="p-4">Metadata</th>
            </tr>
          </thead>
          <tbody data-testid="audit-table">
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No events in the selected window.</td></tr>}
            {pageItems.map((it, i) => (
              <tr key={it.id || i} className={`border-b border-border hover:bg-muted/40 ${it.outcome === "failure" ? "bg-destructive/5" : ""}`}>
                <td className="p-4 mono text-xs whitespace-nowrap">{new Date(it.created_at).toLocaleString()}</td>
                <td className="p-4">
                  <div className="font-semibold">{it.user_name || "—"}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{it.user_role || "anonymous"}</div>
                </td>
                <td className="p-4"><ActionChip action={it.action} /></td>
                <td className="p-4 mono text-xs">
                  {it.resource_type && <div>{it.resource_type}</div>}
                  {it.resource_id && <div className="text-muted-foreground text-[10px] truncate max-w-[180px]">{it.resource_id}</div>}
                </td>
                <td className="p-4 mono text-xs text-muted-foreground">{it.ip || "—"}</td>
                <td className="p-4 mono text-[11px] text-muted-foreground max-w-md truncate">
                  {it.metadata && Object.keys(it.metadata).length > 0
                    ? Object.entries(it.metadata).map(([k,v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`).join(" · ")
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination />
    </PageShell>
  );
}

function Kpi({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className={`border border-border p-5 bg-card ${accent ? "shadow-brut-orange" : ""}`}>
      <div className="overline">{label}</div>
      <div className="font-display text-4xl mt-2 leading-none">{value}</div>
    </div>
  );
}
